import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiError } from '@/api/client'
import { queryKeys, type ImportBatch } from '@/api/queries'
import { Combobox } from '@/components/Combobox'
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog'
import { EmptyState, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { clientsQuery, orgOption, toOptions } from '@/features/directory/api'
import { commitOtwImport, previewOtwImport } from '@/features/orders/import'
import { requireNav } from '@/nav/guard'

export const Route = createFileRoute('/_app/imports/otw')({
  beforeLoad: requireNav('imports'),
  component: OtwImportPage,
})

const CLIENT_KEY = 'trasaflow.import.clientId'
const OUTCOMES = ['NEW', 'CHANGED', 'MISSING', 'INVALID', 'UNCHANGED'] as const
type Outcome = (typeof OUTCOMES)[number]

function storedClientId(): string {
  try {
    return localStorage.getItem(CLIENT_KEY) ?? import.meta.env.VITE_DEFAULT_CLIENT_ID ?? ''
  } catch {
    return import.meta.env.VITE_DEFAULT_CLIENT_ID ?? ''
  }
}

function errorText(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.problem?.detail ?? error.problem?.title ?? fallback
  return fallback
}

function OtwImportPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const ids = { client: useId(), file: useId(), paste: useId(), hint: useId(), filter: useId() }
  const [clientId, setClientId] = useState(storedClientId)
  const [file, setFile] = useState<File | null>(null)
  const [text, setText] = useState('')
  const [batch, setBatch] = useState<ImportBatch | null>(null)
  const [outcome, setOutcome] = useState<Outcome | ''>('')
  const [confirmOpen, setConfirmOpen] = useState(false)

  const clients = useQuery(clientsQuery)
  const clientOptions = toOptions(clients, orgOption)
  const hasInput = file !== null || text.trim() !== ''
  const canPreview = clientId !== '' && hasInput

  const preview = useMutation({
    mutationFn: () => previewOtwImport(clientId, file ? { file } : { text }),
    onSuccess: (result) => {
      try {
        localStorage.setItem(CLIENT_KEY, clientId)
      } catch {
        // Speicher gesperrt: der Auftraggeber muss beim nächsten Mal neu gewählt werden
      }
      setBatch(result)
      setOutcome('')
    },
  })

  const commit = useMutation({
    mutationFn: (id: string) => commitOtwImport(id),
    onSuccess: async (result) => {
      setBatch(result)
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders })
    },
  })

  const summary = batch?.summary ?? {}
  const count = (key: string) => summary[key] ?? 0
  const committed = batch?.status === 'COMMITTED'
  const rows = (batch?.rows ?? []).filter((row) => outcome === '' || row.outcome === outcome)

  return (
    <>
      <PageHeader title={t('imports.title')} description={t('imports.description')} />

      <form
        className="grid max-w-3xl gap-5 rounded-lg border bg-card p-5"
        onSubmit={(event) => {
          event.preventDefault()
          if (canPreview) preview.mutate()
        }}
      >
        <div className="grid gap-1.5">
          <Label htmlFor={ids.client}>{t('imports.clientId')}</Label>
          <Combobox
            id={ids.client}
            value={clientId}
            onChange={setClientId}
            options={clientOptions.options}
            loading={clientOptions.loading}
            aria-describedby={ids.hint}
            emptyText={clientOptions.unavailable ? t('combobox.unavailable') : undefined}
          />
          <p id={ids.hint} className="text-sm text-muted-foreground">
            {t('imports.clientIdHint')}
          </p>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor={ids.file}>{t('imports.file')}</Label>
          <Input
            id={ids.file}
            type="file"
            accept=".xlsx,.csv,.tsv,.txt"
            className="h-auto py-2"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor={ids.paste}>{t('imports.paste')}</Label>
          <Textarea
            id={ids.paste}
            rows={5}
            className="font-mono text-sm"
            value={text}
            placeholder={t('imports.pasteHint')}
            onChange={(e) => setText(e.target.value)}
            disabled={file !== null}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={!canPreview || preview.isPending}>
            {preview.isPending ? t('imports.previewing') : t('imports.preview')}
          </Button>
          {!hasInput && (
            <span className="text-sm text-muted-foreground">{t('imports.noInput')}</span>
          )}
        </div>
        {preview.isError && (
          <p role="alert" className="text-sm text-destructive">
            {errorText(preview.error, t('errors.generic'))}
          </p>
        )}
      </form>

      {batch && (
        <section
          aria-labelledby="import-summary"
          className="mt-8 grid gap-4"
          data-testid="import-result"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 id="import-summary" className="text-lg font-medium">
              {t('imports.summaryTitle')}{' '}
              <Badge variant={committed ? 'default' : 'secondary'}>
                {committed ? t('imports.statusCommitted') : t('imports.statusPreviewed')}
              </Badge>
            </h2>
            {committed ? (
              <Button asChild>
                <Link to="/orders">{t('imports.viewOrders')}</Link>
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={commit.isPending}
              >
                {t('imports.commit')}
              </Button>
            )}
          </div>

          <ul className="flex flex-wrap gap-3" aria-label={t('imports.summaryTitle')}>
            {OUTCOMES.map((key) => (
              <li key={key} className="min-w-32 rounded-lg border bg-card p-3" data-outcome={key}>
                <div className="text-2xl font-semibold">{count(key)}</div>
                <div className="text-sm text-muted-foreground">{t(`imports.outcome.${key}`)}</div>
              </li>
            ))}
          </ul>

          <div className="grid max-w-xs gap-1.5">
            <Label htmlFor={ids.filter}>{t('imports.outcomeFilter')}</Label>
            <select
              id={ids.filter}
              className="h-11 rounded-md border border-input bg-card px-3 text-base"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as Outcome | '')}
            >
              <option value="">{t('common.all')}</option>
              {OUTCOMES.map((key) => (
                <option key={key} value={key}>
                  {t(`imports.outcome.${key}`)}
                </option>
              ))}
            </select>
          </div>

          {rows.length === 0 ? (
            <EmptyState title={t('imports.emptyRows')} />
          ) : (
            <div className="overflow-x-auto rounded-lg border bg-card">
              <table className="w-full text-left" aria-label={t('imports.rowsTitle')}>
                <thead className="bg-muted text-sm">
                  <tr>
                    <th scope="col" className="px-4 py-3">
                      {t('imports.colRow')}
                    </th>
                    <th scope="col" className="px-4 py-3">
                      {t('imports.colRef')}
                    </th>
                    <th scope="col" className="px-4 py-3">
                      {t('imports.colOutcome')}
                    </th>
                    <th scope="col" className="px-4 py-3">
                      {t('imports.colChanges')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.rowNo}-${row.outcome}`} className="border-t align-top">
                      <td className="px-4 py-2">{row.rowNo}</td>
                      <td className="px-4 py-2">{row.externalRef ?? t('common.none')}</td>
                      <td className="px-4 py-2">
                        <Badge variant={row.outcome === 'INVALID' ? 'destructive' : 'secondary'}>
                          {t(`imports.outcome.${row.outcome as Outcome}`)}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {row.error ? (
                          <span className="text-destructive">{row.error}</span>
                        ) : (
                          <DiffList diff={row.diff} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {confirmOpen && batch?.id && (
        <ConfirmActionDialog
          open
          onOpenChange={(open) => !open && setConfirmOpen(false)}
          actionLabel={t('imports.commit')}
          description={t('imports.commitDescription', {
            new: count('NEW'),
            changed: count('CHANGED'),
            missing: count('MISSING'),
          })}
          onConfirm={async () => {
            await commit.mutateAsync(batch.id as string)
          }}
        />
      )}
    </>
  )
}

function DiffList({ diff }: { diff?: Record<string, unknown> }) {
  const { t } = useTranslation()
  const entries = Object.entries(diff ?? {})
  if (entries.length === 0) return <span className="text-muted-foreground">{t('common.none')}</span>
  return (
    <ul className="grid gap-0.5">
      {entries.map(([field, change]) => {
        const [from, to] = Array.isArray(change) ? change : [null, change]
        return (
          <li key={field}>
            {t('imports.diff', { field, from: String(from ?? '∅'), to: String(to ?? '∅') })}
          </li>
        )
      })}
    </ul>
  )
}
